import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DataStore } from '@common/data-models/data-store/data-store';
import { ErrorTypes } from '@common/data-models/misc/errors';
import { HelloResults } from '@common/data-models/service/hello-results';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ILogonService } from '@common/interfaces/i-logon-service';
import { Simple3Des } from '@common/utils/simple-3des';
import { Store } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { ReplaySubject } from 'rxjs';

import { AsyncServiceBase } from './async-service-base';
import { NomWebServicesJson } from './nom-web-services-json';

@Injectable({ providedIn: 'root' })
export class LogonService implements ILogonService {
    private store: Store = inject(Store);

    constructor(public nomWebServicesJson: NomWebServicesJson) {
        this.nomWebServicesJson.logonService = this;
    }

    public username: string = '';
    public password: string = '';
    public rememberMe: boolean = false;

    public hasActiveSession = new ReplaySubject<void>(1);
    public logonPassed = false;

    protected dayOfYear(d: Date): number {
        const yn = d.getFullYear();
        const mn = d.getMonth();
        const dn = d.getDate();
        // noon on Jan. 1
        const d1 = new Date(yn, 0, 1, 12, 0, 0);
        // noon on input date
        const d2 = new Date(yn, mn, dn, 12, 0, 0);
        const ddiff = Math.round((d2.getTime() - d1.getTime()) / 864e5);
        return ddiff + 1;
    }

    public async getProductsCount(): Promise<ServiceResponse<any>> {
        try {
            await this.nomWebServicesJson.getActiveServiceAddress();
            await this.nomWebServicesJson.get('/api/Navigation', {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (e) {
            this.nomWebServicesJson.showErrorModal(
                {
                    message: 'Failed to load products count!',
                    detailMessage: `Failed to load products count! \n\rStackTrace: NomWebServicesJson.getProducsCount(); \n\rError: ${e}`,
                },
                ErrorTypes.WARNING
            );
            return new ServiceResponse<any>(false, 'Failed to load Products Count from web service');
        }

        return new ServiceResponse<any>(false, 1);
    }

    public async getSSOSettings(): Promise<ServiceResponse<Array<SSOSettings>>> {
        const res = new ServiceResponse<Array<SSOSettings>>(!!this.nomWebServicesJson.ssoProviders, this.nomWebServicesJson.ssoProviders);
        if (!res.status) {
            this.nomWebServicesJson.showErrorModal(
                {
                    message: 'Failed to load SSO settings!',
                    detailMessage: 'Failed to load SSO settings!',
                },
                ErrorTypes.WARNING
            );
            return new ServiceResponse<Array<SSOSettings>>(false, 'Failed to load SSO list from web service');
        }
        return res;
    }

    public async getSSOConfigSettings(): Promise<ServiceResponse<SSOConfigSettings>> {
        const res = new ServiceResponse<SSOConfigSettings>(!!this.nomWebServicesJson.ssoConfigSettings, this.nomWebServicesJson.ssoConfigSettings);
        if (!res.status) {
            this.nomWebServicesJson.showErrorModal(
                {
                    message: 'Failed to load SSO settings!',
                    detailMessage: 'Failed to load SSO settings!',
                },
                ErrorTypes.WARNING
            );
            return new ServiceResponse<SSOConfigSettings>(false, 'Failed to load SSO list from web service');
        }
        return res;
    }

    public async logoutSSO(provider: string, payload: string): Promise<ServiceResponse<void>> {
        try {
            await this.nomWebServicesJson.getActiveServiceAddress();

            const Encryptor = new Simple3Des('@pM0B1L3Z1pD!sk2k7L3tMe!n6');
            const encryptData = `@pM0B1L3!5TheB35t|${this.dayOfYear(new Date())}`;
            const devKey = Encryptor.encryptData(encryptData);

            await this.nomWebServicesJson.post(
                '/api/LogoutSSO',
                {
                    domainName: environment.default_domain,
                    devKey,
                    iClientType: 14,
                    ssoToken: payload,
                    ssoProvider: provider,
                },
                {
                    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                    responseType: 'json',
                    observe: 'response',
                }
            );
        } catch (e) {
            this.nomWebServicesJson.clearSessionData();
            return new ServiceResponse<void>(false, environment.translated_label_login_error);
        }
        return new ServiceResponse<void>(true);
    }

    public async logonSSO(provider: string, payload: string, deviceTokenId?: string): Promise<ServiceResponse<void>> {
        let response: HttpResponse<any> = null;
        try {
            await this.nomWebServicesJson.getActiveServiceAddress();

            const Encryptor = new Simple3Des('@pM0B1L3Z1pD!sk2k7L3tMe!n6');
            const encryptData = `@pM0B1L3!5TheB35t|${this.dayOfYear(new Date())}`;
            const devKey = Encryptor.encryptData(encryptData);
            let tokenToSend = deviceTokenId || '';
            if (localStorage.getItem('deviceTokenId') && tokenToSend === '') tokenToSend = localStorage.getItem('deviceTokenId');

            response = await this.nomWebServicesJson.post(
                '/api/LogonSSO',
                {
                    domainName: environment.default_domain,
                    devKey,
                    iClientType: 14,
                    ssoToken: payload,
                    ssoProvider: provider,
                    deviceToken: tokenToSend,
                    invitationId: localStorage.getItem('inviteToken'),
                },
                {
                    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                    responseType: 'json',
                    observe: 'response',
                }
            );
        } catch (e) {
            this.nomWebServicesJson.clearSessionData();
            return new ServiceResponse<void>(false, environment.translated_label_login_error);
        }

        this.nomWebServicesJson.NomTokenId = response.headers.get('X-ENPS-TOKEN');
        this.nomWebServicesJson.AppTokenId = response.headers.get('X-ENPS-APP-TOKEN');

        localStorage.removeItem('inviteToken');
        this.nomWebServicesJson.HelloResults = <HelloResults>Object.assign(new HelloResults(), response.body);
        this.nomWebServicesJson.HelloResults.Navigation = response.body.PSTSuiteNavigation;
        this.nomWebServicesJson.HelloResults.CustomCardFields = response.body.PSTCustomCardFields;
        this.nomWebServicesJson.UserId = this.nomWebServicesJson.HelloResults.UserID;

        this.nomWebServicesJson.saveSessionData();
        this.hasActiveSession.next();
        await this.nomWebServicesJson.connectRTU();
        this.logonPassed = true;
        this.nomWebServicesJson.helloResultsLoadedSource.next({ loaded: true, login: true });

        this.nomWebServicesJson.noSession = false;
        return new ServiceResponse<void>(true);
    }

    public async logon(userName: string, password: string, deviceTokenId?: string, domainName?: string): Promise<ServiceResponse<void>> {
        let legacyResponse: HttpResponse<any> = null;
        try {
            await this.nomWebServicesJson.getActiveServiceAddress();
            const userNameEscaped = userName.toUpperCase().replace(/(\s|\.|_)/g, environment.user_id_space_replacement_character);
            this.nomWebServicesJson.UserId = userNameEscaped;
            const Encryptor = new Simple3Des('@pM0B1L3Z1pD!sk2k7L3tMe!n6');
            const encryptData = `@pM0B1L3!5TheB35t|${this.dayOfYear(new Date())}`;
            const devKey = Encryptor.encryptData(encryptData);
            let tokenToSend = deviceTokenId || '';
            if (localStorage.getItem('deviceTokenId') && tokenToSend === '') tokenToSend = localStorage.getItem('deviceTokenId');
            legacyResponse = await this.nomWebServicesJson.post(
                '/api/Logon',
                {
                    staffUserId: userNameEscaped,
                    domainUserId: userNameEscaped,
                    password,
                    domainName: domainName || environment.default_domain,
                    devKey,
                    iClientType: 14,
                    deviceToken: tokenToSend,
                },
                {
                    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                    responseType: 'json',
                    observe: 'response',
                }
            );
        } catch (e) {
            this.nomWebServicesJson.clearSessionData();
            this.nomWebServicesJson.destroySession();
            if (e.status && e.status === 417) {
                return new ServiceResponse<void>(false, environment.translated_label_login_error);
            }
            if ((e._body && e._body.includes('Expired license key')) || (e.error && e.error.includes('Expired license key'))) {
                return new ServiceResponse<void>(false, environment.translated_label_license_expired);
            }
            return new ServiceResponse<void>(false, environment.translated_label_login_error);
        }
        this.nomWebServicesJson.NomTokenId = legacyResponse.headers.get('X-ENPS-TOKEN');
        this.nomWebServicesJson.AppTokenId = legacyResponse.headers.get('X-ENPS-APP-TOKEN');
        const resp: any = legacyResponse.body;
        this.nomWebServicesJson.HelloResults = <HelloResults>Object.assign(new HelloResults(), resp);

        this.nomWebServicesJson.HelloResults.Navigation = resp.PSTSuiteNavigation;
        this.nomWebServicesJson.HelloResults.CustomCardFields = resp.PSTCustomCardFields;

        this.nomWebServicesJson.UserId = this.nomWebServicesJson.HelloResults.UserID;

        this.nomWebServicesJson.Navigation = this.nomWebServicesJson.HelloResults.Navigation;

        this.nomWebServicesJson.saveSessionData();
        this.hasActiveSession.next();
        await this.nomWebServicesJson.connectRTU();
        this.logonPassed = true;
        this.nomWebServicesJson.helloResultsLoadedSource.next({ loaded: true, login: true });

        this.nomWebServicesJson.noSession = false;
        return new ServiceResponse<void>(true);
    }

    public async logonWithToken(userToken: string): Promise<ServiceResponse<void>> {
        try {
            await this.nomWebServicesJson.getActiveServiceAddress();

            const Encryptor = new Simple3Des('@pM0B1L3Z1pD!sk2k7L3tMe!n6');
            const encryptData = `@pM0B1L3!5TheB35t|${this.dayOfYear(new Date())}`;
            const devKey = Encryptor.encryptData(encryptData);
            const clientType = 14;
            const response: HttpResponse<any> = await this.nomWebServicesJson.post(
                '/api/LogonWithToken',
                {
                    userToken,
                    reuseExistingSession: false,
                    devKey,
                    iClientType: clientType,
                },
                {
                    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                    responseType: 'json',
                    observe: 'response',
                }
            );

            this.nomWebServicesJson.NomTokenId = response.headers.get('X-ENPS-TOKEN');
            this.nomWebServicesJson.AppTokenId = response.headers.get('X-ENPS-APP-TOKEN');
            this.nomWebServicesJson.HelloResults = <HelloResults>Object.assign(new HelloResults(), response.body);
            this.nomWebServicesJson.HelloResults.Navigation = response.body.PSTSuiteNavigation;
            this.nomWebServicesJson.HelloResults.CustomCardFields = response.body.PSTCustomCardFields;
            this.nomWebServicesJson.UserId = this.nomWebServicesJson.HelloResults.UserID;

            this.nomWebServicesJson.saveSessionData();
            this.hasActiveSession.next();
            await this.nomWebServicesJson.connectRTU();
            this.logonPassed = true;
            this.nomWebServicesJson.helloResultsLoadedSource.next({ loaded: true, login: true });

            return new ServiceResponse<void>(true);
        } catch (e) {
            this.nomWebServicesJson.clearSessionData();
            this.nomWebServicesJson.destroySession();
            return new ServiceResponse<void>(false, environment.translated_label_login_error);
        }
    }

    public async logout(): Promise<ServiceResponse<void>> {
        const deviceNomId: string = '';
        try {
            DataStore.rtuSubscriptions = {};
            DataStore.realtimeUpdatesProviders.enps.forceDisconnect();
            const response: HttpResponse<any> = await this.nomWebServicesJson.get('/api/Logout', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                params: {
                    deviceToken: deviceNomId,
                },
                observe: 'response',
            });
            AsyncServiceBase.reset();
            if (response.ok) {
                this.nomWebServicesJson.clearSessionData();
                this.nomWebServicesJson.destroySession();
                // this.initialized = false;
                this.nomWebServicesJson.noSession = true;
                return new ServiceResponse<void>(true);
            }
            this.nomWebServicesJson.showErrorModal(
                {
                    message: 'Logout error',
                },
                ErrorTypes.WARNING
            );
            this.nomWebServicesJson.clearSessionData();
            this.nomWebServicesJson.destroySession();
            return new ServiceResponse<void>(false, 'Logout error');
        } catch (e) {
            this.nomWebServicesJson.showErrorModal(
                {
                    message: 'Logout error',
                    detailMessage: `Logout error. \n\rStackTrace: NomWebServicesJson.logout(); \n\rError: ${e}`,
                },
                ErrorTypes.WARNING
            );
            this.nomWebServicesJson.clearSessionData();
            this.nomWebServicesJson.destroySession();
            return new ServiceResponse<void>(false, 'Logout error');
        }
    }

    public async ping(): Promise<ServiceResponse<void>> {
        const res = await this.nomWebServicesJson.ping();
        if (res.status) {
            this.hasActiveSession.next();
            await this.nomWebServicesJson.connectRTU();
        }
        return res;
    }
}
